import { scope, type } from 'arktype'
import { Guid, Question, Section } from '../../../features/questionnaire'

export const MAX_QUESTION_DEPTH = 3

const generateGuid = (): Guid => crypto.randomUUID()

// Define condition schemas
const ConditionForYesNoSchema = type({
  type: '"yesNoAnswer"',
  answer: '"Yes" | "No"',
}).or(
  type({
    type: '"notRelevantYesNo"',
  }),
)

const ConditionForRangeSchema = type({
  type: '"rangeAnswer"',
  answer: { min: 'number', max: 'number' },
}).or(
  type({
    type: '"notRelevantRange"',
  }),
)

// Define ScaleSchema with constraints
const ScaleSchema = type({
  min: 'number',
  max: 'number',
}).pipe((scale) => {
  if (scale.min < 0) throw new Error('scale.min must be at least 0')
  if (scale.max > 10) throw new Error('scale.max must be at most 10')
  return scale
})

// Define questionScope to handle cyclic types
const questionScope = scope({
  FreeTextQuestion: {
    type: '"freeText"',
    uid: 'string?',
    title: 'string',
    description: 'string | null',
  },
  YesNoSubQuestionItem: {
    condition: ConditionForYesNoSchema,
    question: 'Question', // Cyclic reference to Question
  },
  YesNoQuestion: {
    type: '"yesNo"',
    uid: 'string?',
    title: 'string',
    description: 'string | null',
    notRelevantOption: 'boolean',
    subQuestions: 'YesNoSubQuestionItem[]',
  },
  RangeSubQuestionItem: {
    condition: ConditionForRangeSchema,
    question: 'Question', // Cyclic reference to Question
  },
  RangeQuestion: {
    type: '"range"',
    uid: 'string?',
    title: 'string',
    description: 'string | null',
    notRelevantOption: 'boolean',
    scale: ScaleSchema,
    labels: {
      min: 'string',
      max: 'string',
    },
    subQuestions: 'RangeSubQuestionItem[]',
  },
  Question: 'FreeTextQuestion | YesNoQuestion | RangeQuestion',
})

const QuestionSchema = questionScope.export()

// Define SectionSchema using the Question type from the scope
const SectionSchema = type({
  uid: 'string',
  title: 'string',
  questions: QuestionSchema.Question.array(),
})

// Define QuestionnaireSchema
const QuestionnaireSchema = type({
  title: 'string',
  sections: SectionSchema.array(),
})

// Define parseQuestionnaire to parse and validate the JSON input
const parseQuestionnaire = type('string.json.parse')
  .pipe((val) => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) {
      throw new Error('Invalid questionnaire format: Expected an object.')
    }
    return val
  })
  .to(QuestionnaireSchema)

type ArktypeQuestion = typeof QuestionSchema.Question.infer
type ArktypeSection = typeof SectionSchema.infer
type ArktypeQuestionnaire = typeof QuestionnaireSchema.infer

/* Remove the custom RawQuestion types and use ArkType inferred types */
// --- Transformation with ArkType & UID regeneration ---
const FinalQuestionnaireSchema = parseQuestionnaire.pipe(
  (data: ArktypeQuestionnaire): { title: string; sections: Section[] } => {
    const transformQuestion = (q: ArktypeQuestion, depth: number): Question => {
      const newUid = generateGuid()
      switch (q.type) {
        case 'freeText':
          return {
            type: 'freeText',
            uid: newUid,
            title: q.title,
            description: q.description,
          }
        case 'yesNo':
          return {
            type: 'yesNo',
            uid: newUid,
            title: q.title,
            description: q.description,
            notRelevantOption: q.notRelevantOption,
            subQuestions:
              depth < MAX_QUESTION_DEPTH
                ? q.subQuestions.map((sub) => ({
                    condition: sub.condition,
                    question: transformQuestion(sub.question, depth + 1),
                  }))
                : [],
          }
        case 'range':
          return {
            type: 'range',
            uid: newUid,
            title: q.title,
            description: q.description,
            notRelevantOption: q.notRelevantOption,
            scale: q.scale,
            labels: q.labels,
            subQuestions:
              depth < MAX_QUESTION_DEPTH
                ? q.subQuestions.map((sub) => ({
                    condition: sub.condition,
                    question: transformQuestion(sub.question, depth + 1),
                  }))
                : [],
          }
      }
    }

    const transformSection = (s: ArktypeSection): Section => ({
      uid: generateGuid(),
      title: s.title,
      questions: (s.questions || []).map((q) => transformQuestion(q, 0)),
    })

    const title = data.title
    const sections = (data.sections || []).map(transformSection)
    return { title, sections }
  },
)

// --- Updated parseAndValidateQuestionnaire to use FinalQuestionnaireSchema ---
export const parseAndValidateQuestionnaire = (
  jsonText: string,
): { title: string; sections: Section[] } => {
  try {
    const result = FinalQuestionnaireSchema(jsonText)

    if (result instanceof type.errors) {
      console.error('ArkType Validation Errors:', result.summary)
      throw new Error(`Invalid questionnaire format: ${result.summary}`)
    }

    return result
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    console.error('Questionnaire validation error:', error)
    throw new Error('Invalid questionnaire format')
  }
}
